export const COOKIE_CONSENT_STORAGE_KEY = 'zebra:cookie-consent'

export const COOKIE_CONSENT_CHANGED_EVENT = 'zebra:cookie-consent-changed'

export const COOKIE_CONSENT_TTL_MS = 30 * 24 * 60 * 60 * 1000

export const COOKIE_CONSENT_GROUP_IDS = [
  'essential',
  'analytics',
  'advertising',
] as const

export type CookieConsentGroupId = (typeof COOKIE_CONSENT_GROUP_IDS)[number]

export const ESSENTIAL_COOKIE_CONSENT_ITEM_IDS = ['sentry'] as const

export type EssentialCookieConsentItemId =
  (typeof ESSENTIAL_COOKIE_CONSENT_ITEM_IDS)[number]

export const OPTIONAL_COOKIE_CONSENT_ITEM_IDS = [
  'analytics_storage',
  'ad_storage',
] as const

export type OptionalCookieConsentItemId =
  (typeof OPTIONAL_COOKIE_CONSENT_ITEM_IDS)[number]

export type CookieConsentItemId =
  | EssentialCookieConsentItemId
  | OptionalCookieConsentItemId

export type GtagConsentKey = 'analytics_storage' | 'ad_storage'

export type CookieConsentCatalogItem =
  | { id: EssentialCookieConsentItemId }
  | { id: OptionalCookieConsentItemId; gtagKey: GtagConsentKey }

export type CookieConsentCatalogGroup = {
  id: CookieConsentGroupId
  /** Essential cookies are always granted and cannot be disabled. */
  alwaysGranted?: boolean
  items: CookieConsentCatalogItem[]
}

/**
 * Cookie consent catalog grouped by category.
 * Extend groups/items here when adding new trackers or consent types.
 */
export const COOKIE_CONSENT_CATALOG: CookieConsentCatalogGroup[] = [
  {
    id: 'essential',
    alwaysGranted: true,
    items: [{ id: 'sentry' }],
  },
  {
    id: 'analytics',
    items: [{ id: 'analytics_storage', gtagKey: 'analytics_storage' }],
  },
  {
    id: 'advertising',
    items: [{ id: 'ad_storage', gtagKey: 'ad_storage' }],
  },
]

/** User-toggleable consents persisted in localStorage. */
export type CookieConsentChoices = Record<OptionalCookieConsentItemId, boolean>

/** @deprecated Use OPTIONAL_COOKIE_CONSENT_ITEM_IDS */
export const COOKIE_CONSENT_ITEM_IDS = OPTIONAL_COOKIE_CONSENT_ITEM_IDS

export type CookieConsentItemIdLegacy = OptionalCookieConsentItemId

export function createDefaultCookieConsentChoices(): CookieConsentChoices {
  return OPTIONAL_COOKIE_CONSENT_ITEM_IDS.reduce(
    (choices, itemId) => {
      choices[itemId] = false
      return choices
    },
    {} as CookieConsentChoices,
  )
}

export function createAcceptAllCookieConsentChoices(): CookieConsentChoices {
  return OPTIONAL_COOKIE_CONSENT_ITEM_IDS.reduce(
    (choices, itemId) => {
      choices[itemId] = true
      return choices
    },
    {} as CookieConsentChoices,
  )
}

export function getAlwaysGrantedConsentItems(): Record<
  EssentialCookieConsentItemId,
  true
> {
  return ESSENTIAL_COOKIE_CONSENT_ITEM_IDS.reduce(
    (items, itemId) => {
      items[itemId] = true
      return items
    },
    {} as Record<EssentialCookieConsentItemId, true>,
  )
}

export function isOptionalCookieConsentItemId(
  value: string,
): value is OptionalCookieConsentItemId {
  return (OPTIONAL_COOKIE_CONSENT_ITEM_IDS as readonly string[]).includes(value)
}

export function isEssentialCookieConsentItemId(
  value: string,
): value is EssentialCookieConsentItemId {
  return (ESSENTIAL_COOKIE_CONSENT_ITEM_IDS as readonly string[]).includes(value)
}

export function isCookieConsentItemId(
  value: string,
): value is CookieConsentItemId {
  return isOptionalCookieConsentItemId(value) || isEssentialCookieConsentItemId(value)
}

export function getOptionalItemsFromCatalog(): OptionalCookieConsentItemId[] {
  return COOKIE_CONSENT_CATALOG.flatMap((group) =>
    group.items
      .filter(
        (item): item is { id: OptionalCookieConsentItemId; gtagKey: GtagConsentKey } =>
          'gtagKey' in item,
      )
      .map((item) => item.id),
  )
}

export function getGtagKeysFromCatalog(): GtagConsentKey[] {
  return COOKIE_CONSENT_CATALOG.flatMap((group) =>
    group.items
      .filter(
        (item): item is { id: OptionalCookieConsentItemId; gtagKey: GtagConsentKey } =>
          'gtagKey' in item,
      )
      .map((item) => item.gtagKey),
  )
}
